import { useFpB2bCouponService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupon-service'
import { useFpB2bCoupons } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupons'
import { CouponCategories } from '@cart-ui/CouponCategories/CouponCategories'
import { CouponTabValue, useCouponCategories } from '@cart-ui/CouponCategories/useCouponCategories'
import { CouponList, type CouponListProps } from '@cart-ui/CouponList/CouponList'
import { type CountryCode, type ProjectCode } from '@constants'
import { useDialog } from '@dni-hooks/useDialog'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { Button } from '@pets-ui/Button/Button'
import { DialogActions } from '@pets-ui/DialogActions/DialogActions'
import { DialogContent } from '@pets-ui/DialogContent/DialogContent'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { Divider } from '@pets-ui/Divider/Divider'
import { ResponsiveDialog } from '@pets-ui/ResponsiveDialog/ResponsiveDialog'
import { Tab } from '@pets-ui/Tab/Tab'
import { TabContext } from '@pets-ui/TabContext/TabContext'
import { TabList } from '@pets-ui/TabList/TabList'
import { TabPanel } from '@pets-ui/TabPanel/TabPanel'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom-v5'
import { useStmCart } from '../../CartProvider'
import { DialogCouponManualInput } from './DialogCouponManualInput'

export const DIALOG_COUPON = 'DIALOG_COUPON'

export interface DialogCouponProps {
  campaignCode?: string
  campaignId: number
  projectCode: ProjectCode
  countryCode: CountryCode
  currency?: string
}

const DialogCouponContent = (props: DialogCouponProps) => {
  const { campaignCode, campaignId, projectCode, countryCode, currency } = props
  const dialogContentRef = useRef<HTMLDivElement>(null)
  const history = useHistory()
  const { basePath, platform } = useStmCart()
  const { closeDialog } = useDialog(DIALOG_COUPON)
  const { availableMergedCoupons } = useFpB2bCoupons(campaignId, projectCode, countryCode, campaignCode, platform)
  const { removeManualInputCouponIds } = useFpB2bCouponService(
    campaignId,
    projectCode,
    countryCode,
    campaignCode,
    platform,
  )
  const {
    categories,
    selectedCategory,
    setSelectedCategory,
    availableCouponsForList,
    handleChangeCategory,
    handleTabChange,
    tabValue,
  } = useCouponCategories(availableMergedCoupons)
  const isMobile = useResponsive('mobile')

  /**
   * Improve UX by scrolling to the top and focus on the first item when the category changes
   */
  useEffect(() => {
    if (categories.length > 1 && categories[0]?.value) {
      setSelectedCategory(categories[0]?.value)
      dialogContentRef.current?.scrollTo({ top: 0 })
    }
  }, [categories, setSelectedCategory])

  const handleCouponDelete: CouponListProps['onManualCouponDelete'] = (coupon) => {
    removeManualInputCouponIds([coupon.coupon_id])
  }

  const handleCouponGoShopping: CouponListProps['onGoShopping'] = () => {
    closeDialog()
    history.push(`${basePath}/product-catalog`)
  }

  const handleCouponApply = useCallbackEvent((coupon_id: number) => {
    // TODO: GA event — sendApplyCouponEvent
    void coupon_id
  })

  return (
    <TabContext value={tabValue}>
      <TabList variant="fullWidth" onChange={handleTabChange}>
        <Tab value={CouponTabValue.AUTO_COUPON}>優惠活動</Tab>
        <Tab value={CouponTabValue.MANUAL_INPUT}>優惠碼</Tab>
      </TabList>
      <TabPanel value={CouponTabValue.AUTO_COUPON}>
        <CouponCategories
          categories={categories}
          value={selectedCategory}
          onChange={handleChangeCategory}
        />
      </TabPanel>
      <DialogContent backgroundColor={colors.grey100} rootRef={dialogContentRef} height={isMobile ? '100vh' : '70vh'}>
        <VStack gap={4} mt={4}>
          <TabPanel value={CouponTabValue.MANUAL_INPUT}>
            <DialogCouponManualInput campaignId={campaignId} projectCode={projectCode} countryCode={countryCode} campaignCode={campaignCode} onApplySuccess={handleCouponApply} />
          </TabPanel>
          <CouponList
            coupons={availableCouponsForList}
            currency={currency}
            onManualCouponDelete={handleCouponDelete}
            onGoShopping={handleCouponGoShopping}
          />
          {availableCouponsForList && <Text variant="caption" color={colors.grey500} align="center">- 沒有更多優惠折扣了 -</Text>}
        </VStack>
      </DialogContent>
    </TabContext>
  )
}

export const DialogCoupon = (props: DialogCouponProps) => {
  const { campaignCode, campaignId, projectCode, countryCode, currency } = props
  const { open, closeDialog } = useDialog(DIALOG_COUPON)
  const isMobile = useResponsive('mobile')

  return (
    <ResponsiveDialog open={open} onClose={closeDialog} fullWidth maxHeight="calc(90% - 64px)">
      <DialogHeader title="優惠折扣" onClose={closeDialog} />
      <Divider />
      <DialogCouponContent
        campaignCode={campaignCode}
        campaignId={campaignId}
        projectCode={projectCode}
        countryCode={countryCode}
        currency={currency}
      />
      <DialogActions>
        <Button onClick={closeDialog} fullWidth={isMobile}>
          確認
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  )
}
